/**
 * Full-screen hero section with crossfading Ken Burns slideshow.
 * Two persistent image layers alternate opacity to create smooth transitions.
 * The band logo tracks the mouse cursor with spring-based parallax.
 * First 3 images are eagerly preloaded; the rest load on demand via BlurImage.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import logoImage from "@/assets/thursdayjam-logo.png?w=800&format=webp";
import HeroEffects from "./HeroEffects";
import BlurImage from "./BlurImage";

// Import all hero images
import hero01 from "@/assets/hero/hero-01.jpg?format=webp&quality=85";
import hero02 from "@/assets/hero/hero-02.jpg?format=webp&quality=85";
import hero03 from "@/assets/hero/hero-03.jpg?format=webp&quality=85";
import hero04 from "@/assets/hero/hero-04.jpg?format=webp&quality=85";
import hero05 from "@/assets/hero/hero-05.jpg?format=webp&quality=85";
import hero06 from "@/assets/hero/hero-06.jpg?format=webp&quality=85";
import hero07 from "@/assets/hero/hero-07.jpg?format=webp&quality=85";
import hero08 from "@/assets/hero/hero-08.jpg?format=webp&quality=85";
import hero09 from "@/assets/hero/hero-09.jpg?format=webp&quality=85";
import hero10 from "@/assets/hero/hero-10.jpg?format=webp&quality=85";
import hero11 from "@/assets/hero/hero-11.jpg?format=webp&quality=85";
import hero12 from "@/assets/hero/hero-12.jpg?format=webp&quality=85";
import backgroundImage from "@/assets/background.jpg?format=webp&quality=85";
import liveSession from "@/assets/live-session.jpg?w=1920&format=webp";

const heroImages = [
  hero01, hero02, hero03, hero04, hero05, hero06, 
  hero07, hero08, hero09, hero10, hero11, hero12,
  backgroundImage, liveSession
];

// Only preload the first 3 images eagerly; rest load on demand via BlurImage
let allPreloaded = false;
const preloadPromises = heroImages.slice(0, 3).map((src) => {
  return new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });
});
Promise.all(preloadPromises).then(() => { allPreloaded = true; });

const SLIDE_DURATION = 6000;
const CROSSFADE_DURATION = 1.5;
// Ken Burns runs longer than the display cycle to ensure continuous motion through crossfade
const KENBURNS_DURATION = (SLIDE_DURATION / 1000) + CROSSFADE_DURATION + 2;

interface LayerState {
  index: number;
  opacity: number;
  z: number;
}

const Hero = () => {
  const shuffledImages = useMemo(() => {
    return [...heroImages].sort(() => Math.random() - 0.5);
  }, []);

  // Once all images are preloaded, skip BlurImage for crossfade layers
  const [imagesReady, setImagesReady] = useState(allPreloaded);
  useEffect(() => {
    if (!imagesReady) {
      Promise.all(preloadPromises).then(() => setImagesReady(true));
    }
  }, [imagesReady]);

  // Inject preload link for the LCP image (logo) so the browser discovers it in the initial document
  useEffect(() => {
    const existing = document.querySelector('link[data-lcp-preload]');
    if (!existing) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = logoImage;
      link.setAttribute('fetchpriority', 'high');
      link.setAttribute('data-lcp-preload', 'true');
      document.head.appendChild(link);
    }
  }, []);

  // Track which layer is currently the "base" (fully visible after transitions settle)
  const [frontLayer, setFrontLayer] = useState<'A' | 'B'>('A');
  const [layerA, setLayerA] = useState<LayerState>({ index: 0, opacity: 1, z: 1 });
  const [layerB, setLayerB] = useState<LayerState>({ index: -1, opacity: 0, z: 0 }); // -1 = no image yet
  const isTransitioning = useRef(false);
  const currentIndex = useRef(0);

  // Mouse tracking for logo parallax and scale
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const mouseScale = useMotionValue(1);
  
  const springConfig = { damping: 25, stiffness: 150 };
  const logoX = useSpring(mouseX, springConfig);
  const logoY = useSpring(mouseY, springConfig);
  const logoScale = useSpring(mouseScale, { damping: 30, stiffness: 200 });

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    
    const xOffset = (clientX - innerWidth / 2) / (innerWidth / 2);
    const yOffset = (clientY - innerHeight / 2) / (innerHeight / 2);
    
    // Calculate distance from center (0 = center, 1 = edge)
    const distanceFromCenter = Math.sqrt(xOffset * xOffset + yOffset * yOffset);
    // Scale: 1.04 at center, 1.0 at edges (half of previous 8%)
    const scale = 1 + (1 - Math.min(distanceFromCenter, 1)) * 0.04;
    
    mouseX.set(xOffset * 15);
    mouseY.set(yOffset * 10);
    mouseScale.set(scale);
  }, [mouseX, mouseY, mouseScale]);
  
  const handleMouseLeave = useCallback(() => {
    mouseScale.set(1);
  }, [mouseScale]);

  useEffect(() => {
    if (shuffledImages.length <= 1) return;

    const transition = () => {
      // Prevent overlapping transitions
      if (isTransitioning.current) return;
      isTransitioning.current = true;

      // Calculate next image index
      const nextIndex = (currentIndex.current + 1) % shuffledImages.length;
      currentIndex.current = nextIndex;

      if (frontLayer === 'A') {
        // Layer A is base (opacity 1, z=1), Layer B will overlay
        // Step 1: Arm Layer B with new image at opacity 0, z=2
        setLayerB({ index: nextIndex, opacity: 0, z: 2 });
        
        // Step 2: Next frame, fade in Layer B
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setLayerB(prev => ({ ...prev, opacity: 1 }));
          });
        });

        // Step 3: After crossfade, Layer B becomes the new base
        setTimeout(() => {
          setLayerA(prev => ({ ...prev, opacity: 0, z: 0 }));
          setLayerB(prev => ({ ...prev, z: 1 }));
          setFrontLayer('B');
          isTransitioning.current = false;
        }, CROSSFADE_DURATION * 1000);

      } else {
        // Layer B is base (opacity 1, z=1), Layer A will overlay
        // Step 1: Arm Layer A with new image at opacity 0, z=2
        setLayerA({ index: nextIndex, opacity: 0, z: 2 });
        
        // Step 2: Next frame, fade in Layer A
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setLayerA(prev => ({ ...prev, opacity: 1 }));
          });
        });

        // Step 3: After crossfade, Layer A becomes the new base
        setTimeout(() => {
          setLayerB(prev => ({ ...prev, opacity: 0, z: 0 }));
          setLayerA(prev => ({ ...prev, z: 1 }));
          setFrontLayer('A');
          isTransitioning.current = false;
        }, CROSSFADE_DURATION * 1000);
      }
    };

    const interval = setInterval(transition, SLIDE_DURATION);
    return () => clearInterval(interval);
  }, [frontLayer, shuffledImages.length]);

  const scrollToMusic = () => {
    document.getElementById("vinyl-player")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section 
      className="relative w-full overflow-hidden"
      style={{ height: '100dvh' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Two persistent layers - opacity and z-index controlled for overlay crossfade */}
      <div className="absolute inset-0">
        {/* Layer A */}
        <motion.div
          className="absolute inset-0 ken-burns-container"
          initial={false}
          animate={{ opacity: layerA.opacity }}
          transition={{ duration: CROSSFADE_DURATION, ease: "easeInOut" }}
          style={{ zIndex: layerA.z }}
        >
          {layerA.index >= 0 && (
            imagesReady ? (
              <img
                key={`a-${layerA.index}`}
                src={shuffledImages[layerA.index]}
                alt=""
                className="h-full w-full object-cover grayscale ken-burns-image"
                style={{ animationDuration: `${KENBURNS_DURATION}s` }}
                aria-hidden="true"
              />
            ) : (
              <BlurImage
                key={`a-blur-${layerA.index}`}
                src={shuffledImages[layerA.index]}
                alt=""
                className="h-full w-full object-cover grayscale ken-burns-image"
                style={{ animationDuration: `${KENBURNS_DURATION}s` }}
                aria-hidden="true"
              />
            )
          )}
        </motion.div>

        {/* Layer B */}
        <motion.div
          className="absolute inset-0 ken-burns-container"
          initial={false}
          animate={{ opacity: layerB.opacity }}
          transition={{ duration: CROSSFADE_DURATION, ease: "easeInOut" }}
          style={{ zIndex: layerB.z }}
        >
          {layerB.index >= 0 && (
            imagesReady ? (
              <img
                key={`b-${layerB.index}`}
                src={shuffledImages[layerB.index]}
                alt=""
                className="h-full w-full object-cover grayscale ken-burns-image"
                style={{ animationDuration: `${KENBURNS_DURATION}s` }}
                aria-hidden="true"
              />
            ) : (
              <BlurImage
                key={`b-blur-${layerB.index}`}
                src={shuffledImages[layerB.index]}
                alt=""
                className="h-full w-full object-cover grayscale ken-burns-image"
                style={{ animationDuration: `${KENBURNS_DURATION}s` }}
                aria-hidden="true"
              />
            )
          )}
        </motion.div>

        {/* Dark overlay on top of background images */}
        <div className="absolute inset-0 bg-background/20 z-[3]" />
      </div>

      {/* Stage light effects and film grain */}
      <HeroEffects />

      {/* Heavy vignette overlay */}
      <div className="vignette" />

      {/* Gradient overlays - top for nav readability, bottom for content fade */}
      <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-background/80 via-background/40 to-transparent z-[5]" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background z-[4]" />

      {/* Content */}
      <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 pt-[10vh]">
        {/* Logo Image with mouse parallax */}
        <motion.div
          className="hero-logo-animate"
          style={{ x: logoX, y: logoY, scale: logoScale }}
        >
          <img
            src={logoImage}
            alt="Thursday Jam"
            width={800}
            height={571}
            fetchPriority="high"
            className="max-h-[50vh] w-auto max-w-[90vw] drop-shadow-2xl grayscale md:max-h-[60vh]"
          />
        </motion.div>

        <h1 className="sr-only">Thursday Jam – Blues Rock Band from Berlin, Germany</h1>

        {/* Scroll indicator */}
        <motion.button
          onClick={scrollToMusic}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="group absolute bottom-12 left-1/2 -translate-x-1/2 cursor-pointer"
          aria-label="Scroll to music section"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            className="flex flex-col items-center gap-2"
          >
            <span className="font-display text-xs uppercase tracking-[0.2em] text-foreground/70 transition-colors duration-300 group-hover:text-foreground">
              Scroll
            </span>
            <div className="h-12 w-px bg-gradient-to-b from-foreground/50 to-transparent transition-all duration-300 group-hover:from-foreground group-hover:h-16" />
          </motion.div>
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;
